/**
 * THY AppFeedback tetikleme akışı (096) — dışa açılan tek giriş.
 *
 * Anket, React Query `MutationCache.onSuccess` üzerinden **tek bir merkezden** tetiklenir
 * (`main.tsx`). Bu tasarım iki kuralı yapının kendisinden alır:
 *
 *  - **Yalnız başarıda** (FR-001): `onSuccess` tanımı gereği yalnız başarılı mutation'larda çalışır;
 *    hata `onError` dalına gider ve buraya hiç uğramaz.
 *  - **İşlem başına en fazla bir kez** (FR-017): bir mutation = bir `onSuccess`. Toplu bir işlem
 *    kaç kayıt üretirse üretsin bir kez tetiklenir; ayrıca sayaç tutmaya gerek yoktur.
 *
 * Hangi mutation'ın tetikleyici olduğu `mutation.meta` ile **bildirimsel** olarak işaretlenir —
 * depoda zaten kullanılan bir kalıp (`handledCodes`, `suppressGlobalError`, `proxyBlockedKey`).
 */

import {
  isAppFeedbackSessionEligible,
  primeAppFeedbackToken,
  readAppFeedbackToken,
} from '../../auth/appFeedbackSession'
import { ACTION_STATUS } from '../../features/actions/types'
import { APPFEEDBACK_SITE_ID, appFeedbackBaseUrl, isAppFeedbackEnabled } from './config'
import type { AppFeedbackWidget } from './widgetTypes'
import { reportAppFeedbackIssue, reportMissingConfiguration } from './diagnostics'
import { ensureAppFeedbackWidget, isAppFeedbackVisible } from './loader'
import { buildAppFeedbackTexts } from './texts'

/**
 * Tetikleyici işaret türleri.
 *
 * - `create` — Görev/Alt Görev oluşturma; koşulsuz tetikler.
 * - `status` — statü değiştirme; **yalnız** hedef "Tamamlandı" iken tetikler. Bu hook her statü
 *   geçişinde çalıştığı için işaret tek başına yetmez; hedef `variables`'tan okunur.
 */
export type AppFeedbackTriggerKind = 'create' | 'status'

/** Tetikleyici mutation'ların `meta` alanına konan işaret. */
export interface AppFeedbackMeta {
  appFeedback?: AppFeedbackTriggerKind
}

function readTriggerKind(meta: unknown): AppFeedbackTriggerKind | null {
  if (typeof meta !== 'object' || meta === null) return null
  const kind = (meta as AppFeedbackMeta).appFeedback
  return kind === 'create' || kind === 'status' ? kind : null
}

function readTargetStatusId(variables: unknown): number | null {
  if (typeof variables !== 'object' || variables === null) return null
  const value = (variables as { toStatusId?: unknown }).toStatusId
  return typeof value === 'number' ? value : null
}

/**
 * Bu mutation anketi tetiklemeli mi? (saf karar — yan etkisi yoktur)
 *
 * Ayrı ve dışa açık tutulmasının sebebi test edilebilirlik: FR-017'nin regresyon barajı bu
 * fonksiyona dayanır (`test/appFeedbackTrigger.test.ts`).
 */
export function shouldTriggerAppFeedback(meta: unknown, variables: unknown): boolean {
  const kind = readTriggerKind(meta)
  if (kind === null) return false
  if (kind === 'create') return true
  return readTargetStatusId(variables) === ACTION_STATUS.COMPLETED
}

/** Gönderim taşımasının bir kez sarıldığını işaretler — her tetiklemede yeniden sarmak zincir üretir. */
let submitInstrumented = false

/**
 * Gönderim isteği başarısız olduğunda teşhis izi bırakır (FR-015).
 *
 * Bileşenin kendi hata geri çağrısı **sarmalanır**, yeniden yazılmaz: istek THY'nin kurduğu
 * başlıklar ve gövdeyle gider, biz yalnız hata dalını dinleriz. Kullanıcı yine bileşenin kendi
 * hata ekranını görür — davranış değişmez.
 *
 * > **Sorgulama (`/valid`) isteği bilinçli olarak kapsam dışıdır.** Bileşenin GET yolunda hata
 * > geri çağrısı YOK: yalnız `status == 200` ele alınıyor, başka her durum sessizce düşüyor.
 * > Dinleyebilmek için taşımayı sarmak yetmez, **yeniden yazmak** gerekirdi — o zaman THY'nin
 * > başlıklarını ve istek biçimini biz üstlenirdik ve sonraki sürümde sessizce ayrışabilirdik.
 * > Bedeli kabul edildi: sorgulama tarafındaki arıza yalnız "anket hiç çıkmıyor" olarak görünür.
 * > Eksik yapılandırma vakası ise ayrıca yakalanıyor (`reportMissingConfiguration`), ki teslimin
 * > en olası sessiz arızası odur.
 */
function instrumentSubmitFailure(widget: AppFeedbackWidget): void {
  if (submitInstrumented) return
  const original = widget.httpPostAsync
  if (typeof original !== 'function') return
  submitInstrumented = true
  widget.httpPostAsync = (url, data, onSuccess, onFailed) => {
    original.call(widget, url, data, onSuccess, (error: string) => {
      reportAppFeedbackIssue('Geri bildirim gönderimi başarısız oldu.', error)
      onFailed(error)
    })
  }
}

/**
 * Koşullar sağlanıyorsa anketi başlatır. **Asla hata fırlatmaz.**
 *
 * Kontrol sırası bilinçlidir ve değiştirilmemelidir: `isAppFeedbackEnabled()` en ucuz ve en kesin
 * eleyicidir, ilk o değerlendirilir. Kapalıyken oturum okunmaz, belirteç istenmez, dosya
 * yüklenmez — US2'nin "THY dışında hiçbir olumsuz etki hissedilmez" güvencesi bu sıraya dayanır.
 */
export async function triggerAppFeedback(meta: unknown, variables: unknown): Promise<void> {
  if (!isAppFeedbackEnabled()) {
    // THY alan adı altındayken parametre yoksa bu bilinçli kapatma değil, eksik yapılandırmadır.
    reportMissingConfiguration()
    return
  }
  if (!shouldTriggerAppFeedback(meta, variables)) return
  if (!isAppFeedbackSessionEligible()) return

  try {
    // Belirteç `init()` ÖNCESİNDE hazırlanır: bileşen onu eşzamanlı ister (bkz. widgetTypes.ts).
    if (!(await primeAppFeedbackToken())) return

    // O3: Açık bir anket varken `init()` çağırmak kök elementin içeriğini YENİDEN YAZAR — kullanıcının
    // yarım yazdığı yorum kaybolur ve 240 sn'lik sayaç sıfırlanır. Art arda iki Görev tamamlamak bunu
    // kolayca tetikler. Zaten açık bir anket varken yenisini sormanın kazancı da yok.
    if (isAppFeedbackVisible()) return

    const widget = await ensureAppFeedbackWidget()
    if (!widget) {
      reportAppFeedbackIssue('Betik yüklendi ancak window.appFeedback tanımlanmadı.')
      return
    }

    instrumentSubmitFailure(widget)
    Object.assign(widget, buildAppFeedbackTexts())
    widget.apiUrl = appFeedbackBaseUrl()
    // Bileşenin kendi belirteç okuyucusu bizim anahtar biçimimizi çözemiyor (research.md K-03).
    widget.getToken = readAppFeedbackToken

    widget.init(APPFEEDBACK_SITE_ID)
  } catch (error: unknown) {
    // Kullanıcının asıl işi (Görev oluşturma/tamamlama) bu noktada ZATEN BİTMİŞTİR. Anketin
    // patlaması o akışı bozmamalı ve kullanıcıya hiçbir şey gösterilmemelidir (FR-007).
    reportAppFeedbackIssue('Anket başlatılamadı.', error)
  }
}
